// Improved Stage1 Component with Enhanced Information
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Slider,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  FormControl,
  Paper,
  Divider,
  Tooltip,
  Chip,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Bolt as EnergyIcon,
  SquareFoot as AreaIcon,
  Height as HeightIcon,
  Savings as SavingsIcon,
  Info as InfoIcon,
  ExpandMore,
} from "@mui/icons-material";

const Stage1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const type = location.pathname.includes("offGrid") ? "offGrid" : "onGrid";

  const [billAmount, setBillAmount] = useState(3000);
  const [area, setArea] = useState("");
  const [unit, setUnit] = useState("sqft");
  const [capacity, setCapacity] = useState(3);
  const [savings, setSavings] = useState({ monthly: 0, yearly: 0 });
  const [structureHeight, setStructureHeight] = useState("");

  useEffect(() => {
    let newCapacity = 3;
    if (billAmount >= 3000 && billAmount < 5000) newCapacity = 5;
    else if (billAmount >= 5000 && billAmount < 7000) newCapacity = 7;
    else if (billAmount >= 7000)
      newCapacity = 7 + Math.floor((billAmount - 7000) / 500) * 0.5;

    setCapacity(Math.min(newCapacity, 50));
    setSavings({
      monthly: billAmount * 0.8,
      yearly: billAmount * 0.8 * 12,
    });
  }, [billAmount]);

  useEffect(() => {
    if (!area) return;
    const areaNum = parseFloat(area);
    if (areaNum < 200)
      setStructureHeight("10m+ (Better ventilation & tilt space)");
    else if (areaNum < 500) setStructureHeight("6m (Optimized for shading)");
    else setStructureHeight("4m (Standard configuration)");
  }, [area]);

  const handleSubmit = () => {
    navigate("../stage2", {
      state: {
        type,
        billAmount,
        area,
        unit,
        structureHeight,
        capacity,
        savings,
      },
    });
  };

  const sliderToValue = (pos) => {
    if (pos <= 60) return 1000 + (pos / 60) * (10000 - 1000); // 1k to 10k
    if (pos <= 90) return 10000 + ((pos - 60) / 30) * (20000 - 10000); // 10k to 20k
    return 20000 + ((pos - 90) / 10) * (25000 - 20000); // 20k to 25k
  };

  const valueToSlider = (val) => {
    if (val <= 10000) return ((val - 1000) / (10000 - 1000)) * 60;
    if (val <= 20000) return 60 + ((val - 10000) / (20000 - 10000)) * 30;
    return 90 + ((val - 20000) / (25000 - 20000)) * 10;
  };

  return (
    <Box px={2} maxWidth="1000px" mx="auto">
      <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>
        {type === "onGrid" ? "On-Grid" : "Off-Grid"} Solar System Planner
        <Chip
          label={
            type === "onGrid"
              ? "Grid-Tied: Net Metering Enabled"
              : "Battery Backup: Independent"
          }
          color={type === "onGrid" ? "primary" : "secondary"}
          size="small"
          sx={{ ml: 2 }}
        />
      </Typography>

      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" fontWeight={600}>
            What is {type === "onGrid" ? "On-Grid" : "Off-Grid"} Solar?
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary">
            {type === "onGrid"
              ? "On-Grid systems are connected to the utility power grid. They allow you to export excess electricity and earn credits through net metering. Ideal for urban areas with stable grid supply."
              : "Off-Grid systems operate independently of the power grid, using batteries to store excess power. Ideal for remote or rural locations with unreliable or no grid access."}
          </Typography>
        </AccordionDetails>
      </Accordion>

      <div style={{ display: "flex", gap: "1.5rem", alignItems: "stretch" }}>
        <div style={{ flex: 4 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom>
              <EnergyIcon color="primary" sx={{ mr: 1 }} /> Energy Input Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box mb={4}>
              <Typography>
                Monthly Electricity Bill (₹)
                <Tooltip title="Helps us estimate how much energy your system needs to generate">
                  <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              </Typography>
              <Slider
                value={valueToSlider(billAmount)}
                onChange={(e, val) =>
                  setBillAmount(Math.round(sliderToValue(val)))
                }
                min={0}
                max={100}
                step={1}
                valueLabelDisplay="on"
                valueLabelFormat={(val) =>
                  `₹${Math.round(sliderToValue(val)).toLocaleString()}`
                }
                marks={[
                  { value: 0, label: "1k" },
                  { value: 60, label: "10k" },
                  { value: 90, label: "20k" },
                  { value: 100, label: "25k" },
                ]}
              />
            </Box>

            <Box>
              <Typography>
                Available Rooftop Area
                <Tooltip title="Only shadow-free and south-facing areas should be considered">
                  <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FormControl variant="standard">
                        <Select
                          value={unit}
                          onChange={(e) => setUnit(e.target.value)}
                        >
                          <MenuItem value="sqft">sq.ft</MenuItem>
                          <MenuItem value="sqm">sq.m</MenuItem>
                        </Select>
                      </FormControl>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="medium"
              onClick={handleSubmit}
              sx={{ mt: 4 }}
            >
              Continue to Kit Selection 
            </Button>
          </Paper>
        </div>

        <div style={{ flex: 6 }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom>
              Suggested Configuration Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">System Category:</Typography>
                <Typography fontWeight={600}>
                  {type === "onGrid"
                    ? "Grid-Tied with Net Metering"
                    : "Standalone Battery-Based"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Recommended Capacity:</Typography>
                <Typography fontWeight={600} color="primary.main">
                  {capacity} kW
                </Typography>
              </Grid>
            </Grid>

            <Box mt={2}>
              <Typography variant="body2">
                <HeightIcon fontSize="small" sx={{ mr: 1 }} /> Structure
                Recommendation:
              </Typography>
              <Typography fontWeight={600}>
                {area ? structureHeight : "Please enter area for estimation"}
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography variant="body2">
                <SavingsIcon fontSize="small" sx={{ mr: 1 }} /> Estimated
                Savings:
              </Typography>
              {billAmount > 0 && (
                <>
                  <Typography fontWeight={600}>
                    ₹{savings.monthly.toLocaleString()} per month
                  </Typography>
                  <Typography color="text.secondary">
                    ₹{savings.yearly.toLocaleString()} annually
                  </Typography>
                </>
              )}
            </Box>

            <Box
              mt={3}
              p={2}
              bgcolor={theme.palette.grey[100]}
              borderRadius={2}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Tip:</strong> A {capacity}kW system typically requires ~
                {Math.ceil(capacity * 100)} sq.ft of usable space. Ensure the
                area is south-facing and receives direct sunlight.
              </Typography>
            </Box>
          </Paper>
        </div>
      </div>
    </Box>
  );
};

export default Stage1;
